import { isNonNullable, nonNullable } from 'lib/array/arrayUtils'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { isLEHotel } from 'lib/offer/offerTypes'
import {
  displayContextBody,
  displayContextField,
  nonEmptyString,
} from './offerPageContextFormatting'
import type {
  AccommodationOfferPageAssistantFinePrintSectionRequest,
} from './offerPageContextTypes'

export type OfferPageFinePrintNormalization = 'legacy' | 'snapshot'

export type BuildOfferPageFinePrintSectionsOptions = {
  normalization?: OfferPageFinePrintNormalization
}

function buildFinePrintSection(
  title: string | undefined,
  body: string | undefined | null,
  normalization: OfferPageFinePrintNormalization = 'legacy',
): AccommodationOfferPageAssistantFinePrintSectionRequest | undefined {
  const normalizedBody = normalization === 'legacy' ? displayContextBody(body) : nonEmptyString(body)
  if (!normalizedBody) {
    return undefined
  }

  const normalizedTitle = normalization === 'legacy' ? displayContextField(title) : nonEmptyString(title)
  return {
    ...(normalizedTitle ? { title: normalizedTitle } : {}),
    body: normalizedBody,
  }
}

function normalizeFinePrintTitleKey(title: string): string {
  return title.toLowerCase().replace(/\s+/g, '-').trim()
}

function finePrintSectionDedupeKey(
  section: AccommodationOfferPageAssistantFinePrintSectionRequest,
  index: number,
): string {
  const normalizedTitle = section.title ? normalizeFinePrintTitleKey(section.title) : undefined
  if (normalizedTitle) {
    return normalizedTitle
  }

  return `untitled:${index}:${section.body}`
}

/**
 * Property's CMS-authored `finePrintSubsections` are meant to override the
 * legacy structured/top-level fine-print fields when they share a title (e.g.
 * a "Fine print" subsection replacing the top-level `property.finePrint`) —
 * deliberate first-occurrence-wins semantics, not a generic anti-collision
 * guard. `subsectionRows` are listed first below so they win.
 */
function dedupeFinePrintSections(
  sections: Array<AccommodationOfferPageAssistantFinePrintSectionRequest>,
): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  const seen = new Set<string>()

  return sections.filter((section, index) => {
    const key = finePrintSectionDedupeKey(section, index)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

/**
 * Unlike Property's subsections, a bedbank offer's arbitrary supplier-controlled
 * `finePrint.policies` keys have no override relationship with the fixed
 * section titles they're merged with — a supplier policy titled "Fees" is
 * distinct content from the hardcoded booking-fees section, not a replacement
 * for it. Relabels (never drops) a later title on collision so both sections
 * stay distinguishable instead of appearing as an unexplained duplicate.
 */
function disambiguateDuplicateFinePrintTitles(
  sections: Array<AccommodationOfferPageAssistantFinePrintSectionRequest>,
): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  const seen = new Set<string>()

  return sections.map((section, index) => {
    const key = finePrintSectionDedupeKey(section, index)
    if (!seen.has(key)) {
      seen.add(key)
      return section
    }

    // Untitled sections always key on their own index (see
    // finePrintSectionDedupeKey), so they can never reach this branch.
    if (!section.title) {
      return section
    }

    // Checked against the same `seen` set real titles are added to (not a
    // separate suffix-only namespace) — otherwise an arbitrary supplier
    // policy title that happens to already look like a suffixed title (e.g.
    // literally "Fees (2)") could go unnoticed and collide with the very
    // suffix this loop picks for an unrelated "Fees" collision.
    let suffix = 2
    let candidateTitle = `${section.title} (${suffix})`
    while (seen.has(normalizeFinePrintTitleKey(candidateTitle))) {
      suffix += 1
      candidateTitle = `${section.title} (${suffix})`
    }
    seen.add(normalizeFinePrintTitleKey(candidateTitle))
    return { ...section, title: candidateTitle }
  })
}

// Matches the hardcoded note the real `ThingsToKnow.tsx` always shows in place of a cancellation
// policy for always-on offers -- these are dynamically priced, so there's no single fixed
// cancellation policy to describe (it varies per rate).
const ALWAYS_ON_CANCELLATION_NOTE = 'Please note cancellation policies vary depending on the rate selected, please choose your package carefully. See full booking terms and conditions'

/**
 * Matches `ThingsToKnow.tsx` (the real rendered component) field-for-field: `structuredFinePrint`
 * is the real source of truth, not the raw legacy `offer.finePrint` blob -- the previous version of
 * this function ran regex extraction over that raw blob using utils explicitly labelled temporary
 * and otherwise only used by the order-view `OrderFinePrint.tsx` (a different surface entirely),
 * which could show content the live offer page never renders. Taxes & fees is nested inside the
 * "Fine print" row's content on the real page, not a separate section.
 */
function buildLeHotelFinePrintSections(
  offer: App.HotelOffer | undefined,
  normalization: OfferPageFinePrintNormalization = 'legacy',
): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  if (!offer) {
    return []
  }

  const cancellationText = offer.type === OFFER_TYPE_ALWAYS_ON
    ? ALWAYS_ON_CANCELLATION_NOTE
    : offer.structuredFinePrint?.cancellationText
  const dateChangesText = offer.structuredFinePrint?.dateChangesText
  const finePrintText = offer.structuredFinePrint?.finePrint ?? offer.finePrint
  const finePrintWithTaxesAndFees = offer.property?.taxesAndFeesContent
    ? [`Taxes & fees:\n${offer.property.taxesAndFeesContent}`, finePrintText].filter(isNonNullable).join('\n\n')
    : finePrintText

  return [
    buildFinePrintSection('Check-in', offer.checkInTime, normalization),
    buildFinePrintSection('Check-out', offer.checkOutTime, normalization),
    buildFinePrintSection('Cancellation policy', cancellationText, normalization),
    buildFinePrintSection('Date changes', dateChangesText, normalization),
    buildFinePrintSection('Fine print', finePrintWithTaxesAndFees, normalization),
  ].filter(isNonNullable)
}

function buildBedbankFinePrintSections(
  offer: App.BedbankOffer | undefined,
  normalization: OfferPageFinePrintNormalization = 'legacy',
): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  const finePrint = offer?.property.finePrint
  if (!offer) {
    return []
  }

  const promotionCancellation = offer.promotions
    .map(promotion => nonEmptyString(promotion.cancellationPolicy))
    .filter(isNonNullable)
    .join('\n\n')
  const promotionFinePrint = offer.promotions.find(promotion => nonEmptyString(promotion.finePrint))?.finePrint
  const checkIn = [
    finePrint?.checkIn?.beginTime ? `Check-in starts at ${finePrint.checkIn.beginTime}` : undefined,
    finePrint?.checkIn?.endTime ? `Check-in ends at ${finePrint.checkIn.endTime}` : undefined,
  ].filter(isNonNullable).join('\n')
  const checkInInstructions = [
    finePrint?.checkIn?.instructions,
    finePrint?.checkIn?.specialInstructions,
  ].map(value => nonEmptyString(value)).filter(isNonNullable).join('\n\n')
  const fees = [
    finePrint?.fees?.mandatory,
    finePrint?.fees?.optional,
  ].map(value => nonEmptyString(value)).filter(isNonNullable).join('\n\n')
  const policies = finePrint?.policies ?
      Object.entries(finePrint.policies)
        .map(([key, value]) => buildFinePrintSection(key, value, normalization))
        .filter(isNonNullable) :
      []

  return disambiguateDuplicateFinePrintTitles([
    buildFinePrintSection('Check-in', checkIn, normalization),
    buildFinePrintSection('Check-out', finePrint?.checkOut?.time, normalization),
    buildFinePrintSection('Check-in instructions', checkInInstructions, normalization),
    buildFinePrintSection('Fees', fees, normalization),
    buildFinePrintSection('Pets', finePrint?.pets?.join('\n'), normalization),
    buildFinePrintSection('General', finePrint?.general?.join('\n'), normalization),
    buildFinePrintSection('Cleaning and safety', finePrint?.safety?.join('\n'), normalization),
    ...policies,
    buildFinePrintSection('Cancellation policy', promotionCancellation, normalization),
    buildFinePrintSection('Fine print', offer.property.finePrintOverride ?? promotionFinePrint, normalization),
  ].filter(isNonNullable))
}

/**
 * Matches `PropertyThingsToKnow.tsx` (the real rendered component) exactly: it only ever renders
 * `property.finePrintSubsections`, never the legacy `finePrint`/`taxesAndFeesContent`/
 * `structuredFinePrint` fields directly. A previous version of this function fell back to those
 * legacy fields whenever a same-titled subsection was absent, which could claim "Check-in",
 * "Check-out", "Fees" etc. sections exist as real, rendered targets when the live page shows
 * nothing for them at all (confirmed live: those fields can hold real data even when
 * `finePrintSubsections` doesn't cover them, since the real component simply never reads them).
 */
function buildPropertyFinePrintSections(
  property: App.Property.Property | undefined,
  normalization: OfferPageFinePrintNormalization = 'legacy',
): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  if (!property) {
    return []
  }

  const subsectionRows = property.finePrintSubsections?.map(row => buildFinePrintSection(row.label, row.content.value, normalization)) ?? []
  return dedupeFinePrintSections(nonNullable(subsectionRows))
}

export function buildOfferPageFinePrintSections(
  offerPageState: OfferPageState,
): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  return buildOfferPageFinePrintSectionsFromSources({
    hotelOffer: isLEHotel(offerPageState.offer) ? offerPageState.offer : undefined,
    bedbankOffer: offerPageState.bedbankOffer,
    property: offerPageState.property,
  })
}

export function buildOfferPageFinePrintSectionsFromSources({
  hotelOffer,
  bedbankOffer,
  property,
}: {
  hotelOffer?: App.HotelOffer
  bedbankOffer?: App.BedbankOffer
  property?: App.Property.Property
}, options?: BuildOfferPageFinePrintSectionsOptions): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  const normalization = options?.normalization ?? 'legacy'

  if (property) {
    return buildPropertyFinePrintSections(property, normalization)
  }

  return [
    ...buildLeHotelFinePrintSections(hotelOffer, normalization),
    ...buildBedbankFinePrintSections(bedbankOffer, normalization),
  ]
}
